import { TitleCasePipe, APP_BASE_HREF, DecimalPipe } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Inject, Injector, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';

import { BehaviorSubject, filter } from 'rxjs';
import { cloneDeep } from 'lodash';

import { LinkedinSubTypeEnum } from '@Common/Basic/enums/linkedin-sub-type.enum';
import { MediaTypeEnum } from '../../../../Common/Basic/enums/media-type.enum';

import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { userPermissionsEnum } from '../../../../Core/Users/enums/user-permissions.enum';

import { MentionBadge, MentionCardComponent, mentionInSelectedFolders } from '../../../../Common/Mentions/components/MentionCard/mention-card.component';
import { Mention } from '../../../../Common/Mentions/models/mention';
import { TVEyesMentionsService } from '../../../../Common/Mentions/services/tveyes-mentions.service';
import { webVideoHelper } from '../../../../Common/Misc/helpers/webVideo/webVideo.helper';
import { IndexHighlightPipe } from '../../../../Common/Misc/pipes/Highlight/indexHighlight.pipe';
import { TwitterAvatarSize } from '../../../../Common/Misc/pipes/TwitterAvatarSize/twitter-avatar-size.pipe';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { TranslateMentionsService } from '../../../../Common/Mentions/services/translate-mentions.service';

import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';

import { TwitterSubTypeEnum } from '../../../../Common/Basic/enums/twitter-sub-type.enum';
import { PostViewerDialogComponent } from '../../../../Common/Dialogs/components/PostViewerDialog/post-viewer-dialog.component';
import { socialThumbnailDefaultImage } from '../../../../Common/Misc/enums/social-thumbnail-default-image';
import { socialNetworkTypeColorMap } from '../../../../Core/Users/data/social-network-type-color-map';
import { Folder } from '../../../../Monitor/Shared/models/folder.model';

@Component({
    selector: 'agpr-social-post-feed-card',
    templateUrl: './social-post-feed-card.component.html',
    styleUrls: ['./social-post-feed-card.component.scss'],
    standalone: false
})
export class SocialPostFeedCardComponent extends MentionCardComponent implements OnInit, OnChanges, AfterViewInit {

    @Input() selectedResults: Mention[] = [];
    @Input() showCheckbox: boolean = false;
    @Input() index: number;
    @Input() selectedFolder: Folder;
    @Input() allSelected: boolean = false;

    @Output() selectionChanged: EventEmitter<Mention[]> = new EventEmitter<Mention[]>();

    mediaType: MediaTypeEnum;
    tweetType: TwitterSubTypeEnum;
    linkedinType: LinkedinSubTypeEnum;

    impressionLabel: string = 'Social.postFeed.followers';
    embedVideoUrl: string = '';

    overlayContainer: Element;

    iframeTitle: string;
    userLanguage: string;
    concurrentTranslation: number = 0;
    openAiPermission: boolean = false;
    mentionInFolderBadge: MentionBadge;

    private titleCasePipe = new TitleCasePipe();

    readonly maxConcurrentTranslation: number = 3;
    readonly buttonTypes = ButtonTypesEnum;
    readonly mediaTypes = MediaTypeEnum;
    readonly tweetTypeEnum = TwitterSubTypeEnum;
    readonly linkedinTypeEnum = LinkedinSubTypeEnum;
    readonly twitterAvatarSizes = TwitterAvatarSize;
    readonly imageDefault = socialThumbnailDefaultImage;
    readonly socialNetworkTypeColorMap = socialNetworkTypeColorMap;

    constructor(
        protected currentUserStateService: CurrentUserStateService,
        protected coreDialogService: CoreDialogService,
        protected injector: Injector,
        protected indexHighlightPipe: IndexHighlightPipe,
        private ref: ElementRef,
        protected localizationService: LocalizationService,
        protected tvEyesMentionsService: TVEyesMentionsService,
        protected translateService: TranslateMentionsService,
        protected decimalPipe: DecimalPipe,
        protected changeDetectorRef: ChangeDetectorRef,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
    ) {
        super(
            currentUserStateService,
            coreDialogService,
            indexHighlightPipe,
            tvEyesMentionsService,
            translateService,
            localizationService,
            decimalPipe,
            changeDetectorRef,
            appBaseHref
        );
        this.subscriptions.push(
            this.translateService.getLanguage().subscribe((language) => this.userLanguage = language),
            this.translateService.getConcurrentTranslation().subscribe(
                (concurrentTranslation) => this.concurrentTranslation = concurrentTranslation
            ),
            this.currentUserStateService.getPermissions().pipe(filter(x => !!x)).subscribe(permissions => {
                if (permissions?.length) {
                    this.openAiPermission = permissions.includes(userPermissionsEnum.openAI);
                }
            }),
        );
    }
    ngAfterViewInit(): void {
        setTimeout(() => {
            this.collapsedHeight = this.cardContainer.nativeElement.offsetHeight + 'px';
            // check if expand icon button should be active or not, this setTimeOut helps to avoid ExpressionChangedAfterItHasBeenCheckedError
            this.canExpand =
                (this.mainBody.nativeElement.scrollHeight > this.mainBody.nativeElement.offsetHeight) ||
                (!this._showImages && (this.mention.images?.length > 0 || !!this.embedVideoUrl)) ||
                !!this.mention.tweetData?.originalTweetData?.originalText.length;
        }, 0);
    }

    ngOnInit() {
        super.ngOnInit();

        this.overlayContainer = document.getElementById('main-content');

        this.mediaType = this.mention.mediaType.id;
        if (this.mediaType === MediaTypeEnum.YOUTUBE || this.mediaType === MediaTypeEnum.REDDIT) {
            this.impressionLabel = 'Social.postFeed.subscribers';
        }

        if (this.mediaType === MediaTypeEnum.YOUTUBE) {
            this.embedVideoUrl = webVideoHelper.getYoutubeEmbedURL(this.mention.url);
            this.iframeTitle = this.localizationService.transform(
                'Social.postViewer.iframeTitle',
                { media: this.localizationService.transform(`MediaType.${this.mediaType}`)}
            );
        }

        this.tweetType = this.mention?.tweetData?.type;
        this.linkedinType = this.mention?.linkedinData?.type;
    }

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);

        if (this.selectedFolder && this.mention?.folderIds.some(id => id === this.selectedFolder.id)) {
            this.mentionInFolderBadge = cloneDeep(mentionInSelectedFolders);
            this.mentionInFolderBadge.tooltip = this.localizationService.transform(this.mentionInFolderBadge.tooltip) + this.selectedFolder.name;
        } else {
            this.mentionInFolderBadge = null;
        }
    }

    openPostViewer(): void {
        if (!this.mention.translating) {
            const animationTrigger = new BehaviorSubject(
                new FullProfileAnimationModel(
                    FullProfileDialogAnimationsEnum.slideInOutAnimation,
                    OverlayAnimationStateEnum.open
                )
            );

            const ioModel =  {
                inputs: {
                    mention: this.mention,
                    animationTrigger: animationTrigger,
                    overlayContainer: this.overlayContainer
                },
                outputs: {
                    dialogClose: (closeAction) => {
                        // do nothing right now
                    }
                }
            };

            this.coreDialogService.open(PostViewerDialogComponent, ioModel, this.injector);
        }
    }

    postSelectionChanged(): void {
        this.selectionChanged.emit(this.selectedResults);
    }

    navigateTo(clickEvent: PointerEvent): void {
        if (this.mention.socialUrl) {
            window.open(this.mention.socialUrl);
        }
        clickEvent.stopPropagation();
    }

    translate() {
        if (this.concurrentTranslation < this.maxConcurrentTranslation) {
            if (this.mention) {
                this.translateService.setupChatAI(this.mention, null, true);
            }
        }
    }

    revertTranslate() {
        if (this.concurrentTranslation < this.maxConcurrentTranslation) {
            this.translateService.setDefaultTextAlign(this.mention);
            this.mention.showTranslated = false;
            this.mention.translation = null;
        }
    }

    toggleExpanded(clickEvent: any): void {
        clickEvent.stopPropagation();
        if (!this.canExpand) {
            return;
        }
        super.toggleExpanded(clickEvent);
    }

}
